import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => void;
}

interface User {
  id: string;
  username: string;
  role: string;
  token: string;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing session on app initialization
    const initAuth = async () => {
      const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
      
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          // Validate token with backend
          const isValid = await authService.validateToken(userData.token);
          
          if (isValid) {
            setUser(userData);
          } else {
            // Token invalid, clear storage
            localStorage.removeItem('user');
            sessionStorage.removeItem('user');
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
        }
      }
      
      setIsLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (username: string, password: string, remember = false) => {
    const response = await authService.login(username, password);
    const userData = {
      id: response.id,
      username: response.username,
      role: response.role,
      token: response.token
    };
    
    setUser(userData);
    
    // Store user data based on remember preference
    if (remember) {
      localStorage.setItem('user', JSON.stringify(userData));
    } else {
      sessionStorage.setItem('user', JSON.stringify(userData));
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        isAuthenticated: !!user, 
        isLoading, 
        login, 
        logout 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
